// Config
import { msalObj } from './entraConfig';
// APIs
import userAPI from './apiServices/userAPI';
// Utils
import { accountMatchesEnteredEmail, resolveAccountEmail, describeAccountForTelemetry } from './utils/entraAccountMatch';
// Services
import { TelemetryService } from './services/TelemetryService';
// Types

const CONNECTION_ERROR =
  "Could not connect to CMMS. Please check your network connection and try again.";

// Request the `email` scope so the ID token carries the `email` claim. CMMS keys
// Entra users on the Graph `mail` attribute (see EntraSyncService), which surfaces
// in the token's `email` claim. Without this scope the token only has
// `preferred_username` (the UPN), which differs from `mail` in tenants like Adani
// (UPN = employee number, mail = friendly address) and never matches the CMMS record.
const LOGIN_SCOPES = ["openid", "profile", "email"];

// Opt-in, time-boxed diagnostic for the Adani login investigation. OFF unless
// VITE_ENTRA_LOGIN_DIAGNOSTICS is explicitly "on"/"true". When on, the login-claim
// snapshot (which contains PII — see describeAccountForTelemetry) is sent to App
// Insights. Keeping it flagged means it is not permanent, not silently all-users,
// and has a kill-switch: turn it off once the investigation is done.
function loginDiagnosticsEnabled(): boolean {
  const flag = (import.meta.env.VITE_ENTRA_LOGIN_DIAGNOSTICS ?? "").toString().toLowerCase();
  return flag === "on" || flag === "true";
}

function isConnectionTimeout(error: unknown): boolean {
  const err = error as { code?: string; message?: string; response?: unknown };
  return (
    err?.code === "ECONNABORTED" ||
    err?.code === "ERR_NETWORK" ||
    err?.message?.toLowerCase().includes("timeout") ||
    (!err?.response && !!err?.code)
  );
}

// Map known backend errorMessage values (from LoginWithEntraID) to user-facing copy.
// Returns null for unknown values so the caller can fall back to the generic message.
// Keep in sync with Application/impl/Controllers/UserController.cs LoginWithEntraID.
function knownBackendErrorToUserMessage(backendMessage: string | undefined): string | null {
  switch (backendMessage) {
    case "Input token does not contain an email claim.":
      return "Your sign-in token is missing an email address. Contact your IT administrator.";
    case "Input token claims do not match login username.":
      return "The signed-in account does not match the email entered.";
    case "Unknown user.":
      return "This account is not registered in CMMS.";
    case "Inactive user.":
      return "This account is deactivated. Contact your CMMS administrator.";
    case "Authentication method not entra.":
      return "This account is configured for password sign-in, not SSO.";
    default:
      return null;
  }
}

function extractBackendErrorMessage(error: unknown): string | undefined {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object" && "errorMessage" in data) {
    const msg = (data as { errorMessage?: unknown }).errorMessage;
    return typeof msg === "string" ? msg : undefined;
  }
  return undefined;
}

// Companion to EntraLoginAttempt: captures the backend verdict so we can see WHY a
// login failed (claim mismatch vs unknown user vs token validation) rather than only
// the client-side claim shape. Gated by the same diagnostic flag, isolated try/catch.
function trackEntraLoginResult(
  entraUser: import("@azure/msal-browser").AccountInfo | null,
  enteredUsername: string | null,
  outcome: "success" | "failure",
  httpStatus: number | undefined,
  backendMessage: string | undefined,
): void {
  if (!loginDiagnosticsEnabled()) return;
  try {
    const snapshot = entraUser
      ? describeAccountForTelemetry(entraUser, enteredUsername)
      : { enteredEmail: (enteredUsername ?? "").trim().toLowerCase() };
    TelemetryService.trackEvent("EntraLoginResult", {
      ...snapshot,
      outcome,
      httpStatus: httpStatus !== undefined ? String(httpStatus) : "",
      backendMessage: backendMessage ?? "",
    });
  } catch (telemetryErr) {
    console.error("EntraLoginResult telemetry failed", telemetryErr);
  }
}

export function handleEntra() {

  const login = async () => {
    try {
      if (!msalObj) {
        throw new Error("MSAL not initialized - cannot reach Entra");
      }
      await msalObj.loginRedirect({ scopes: LOGIN_SCOPES });
    } catch {
      sessionStorage.setItem("authError", "Could not start sign-in. Please try again.");
    }
  }

  const logout = () => {
    try {
      sessionStorage.clear();

      if (!msalObj) {
        throw new Error('MSAL not initialized - cannot reach Entra');
      }

      msalObj.logoutRedirect({
        onRedirectNavigate: () => false,
      });
    } catch {
      sessionStorage.setItem("authError", "Could not sign out. Please try again.");
    }
  }

  const handleRedirect = async () => {
    let entraUser = null;
    let entraToken = null;

    try {
      await msalObj.handleRedirectPromise();
      const existing_token = sessionStorage.getItem("sessionToken");
      const previouslyLoggedIn = (existing_token) ? true : false;

      if (!previouslyLoggedIn && msalObj.getAllAccounts().length > 0) {
        entraUser = msalObj.getAllAccounts()[0];
        const enteredUsername = sessionStorage.getItem("username");

        // Diagnostic for tenants where the login identifier differs from the CMMS-stored
        // email (e.g. Adani UPN vs Graph `mail`): capture what the token actually carries,
        // on both the pass and fail paths. Gated behind an opt-in flag (PII), and isolated
        // in its own try/catch so a telemetry failure can never break the login flow.
        if (loginDiagnosticsEnabled()) {
          try {
            TelemetryService.trackEvent("EntraLoginAttempt", describeAccountForTelemetry(entraUser, enteredUsername));
          } catch (telemetryErr) {
            console.error("EntraLoginAttempt telemetry failed", telemetryErr);
          }
        }

        // The `enteredUsername &&` guard preserves silent-SSO flows: when no email was
        // typed (sessionStorage["username"] absent), we skip the match check entirely.
        if (enteredUsername && !accountMatchesEnteredEmail(entraUser, enteredUsername)) {
          // Show the resolved account email rather than the raw username — B2B guests have
          // `user_home.com#EXT#@host.onmicrosoft.com` as username, which is alarming to users.
          const shownAccount = resolveAccountEmail(entraUser, enteredUsername).toLowerCase();
          logout();
          sessionStorage.setItem("authError", `The signed-in account "${shownAccount}" does not match the email "${enteredUsername.toLowerCase()}" entered.`);
          return;
        }

        // Include the OIDC scopes alongside user.read so the silently-acquired ID token
        // also carries the `email` claim, consistent with the login request above.
        const request = Object.assign({ scopes: ['user.read', ...LOGIN_SCOPES] }, { account: entraUser });
        const tokenResponse = await msalObj.acquireTokenSilent(request);
        entraToken = tokenResponse.idToken as string;

        // Send the resolved email to the backend, not the raw MSAL username. For B2B
        // guests `username` is the `#EXT#` UPN, which won't match the CMMS user record.
        const accountEmail = resolveAccountEmail(entraUser, enteredUsername);
        const response = await userAPI.loginWithEntraId(accountEmail, entraToken);
        if (response.status == 200) {
          trackEntraLoginResult(entraUser, enteredUsername, "success", 200, undefined);
          sessionStorage.removeItem("authError");
          sessionStorage.removeItem("username");
          //
          // Important!!! Save session token. It will be used in all API call
          //
          sessionStorage.setItem("sessionToken", response.data.token);
          sessionStorage.setItem("sessionExpire", response.data.expirationTS);
          // Bootstrap continues from here — services load with the new token, then app mounts
        }
        else {
          // Defensive branch — axios throws on non-2xx by default and lands in catch.
          // Kept for the unlikely 2xx-but-not-200 case (custom validateStatus, gateway).
          const backendMessage = typeof response.data === "object"
            ? (response.data?.errorMessage as string | undefined)
            : undefined;
          console.error("handleEntra.handleRedirect non-200", { status: response.status, backendMessage });
          trackEntraLoginResult(entraUser, enteredUsername, "failure", response.status, backendMessage);
          logout();
          sessionStorage.setItem(
            "authError",
            knownBackendErrorToUserMessage(backendMessage) ?? "Sign-in failed. Please try again.",
          );
        }
      }
    } catch (error) {
      const backendMessage = extractBackendErrorMessage(error);
      const httpStatus = (error as { response?: { status?: number } })?.response?.status;
      console.error("handleEntra.handleRedirect failed", { backendMessage, httpStatus, error });
      trackEntraLoginResult(
        entraUser,
        sessionStorage.getItem("username"),
        "failure",
        httpStatus,
        backendMessage,
      );
      logout();
      const userMessage =
        knownBackendErrorToUserMessage(backendMessage) ??
        (isConnectionTimeout(error) ? CONNECTION_ERROR : "Sign-in failed. Please try again.");
      sessionStorage.setItem("authError", userMessage);
    }
  }

  return { login, logout, handleRedirect };
}
